import {
  CONTACT_OBJECT_TYPE_ID,
  FIELD,
  SUBMIT_ENDPOINT,
  UTM_FIELDS,
  UtmField,
} from "./prepForm.constants";
import { getHubspotUtk } from "./prepForm.browser";

export interface PrepFormValues {
  firstName: string;
  lastName: string;
  email: string;
  mobile: string; // already E.164
  school: string;
  serviceId: string;
  brand: string;
  zip?: string;
  firstYear: string;
  utms: Partial<Record<UtmField, string>>;
}

export interface PrepFormConfig {
  portalId?: string;
  formGuid?: string;
  consentToProcessText: string;
  communicationsConsentText: string;
  communicationsConsent: boolean;
  subscriptionTypeId: number;
  omitHutk?: boolean; // shared-device (info-night iPad) placements must not send hutk
}

export interface SubmitResult {
  ok: boolean;
  dryRun?: boolean;
  status?: number;
  error?: string;
}

type HsField = { objectTypeId: string; name: string; value: string };

// Brief: omit blanks, never send "".
const pushIfPresent = (fields: HsField[], name: string, value?: string) => {
  if (value != null && String(value).trim() !== "") {
    fields.push({ objectTypeId: CONTACT_OBJECT_TYPE_ID, name, value: String(value) });
  }
};

export const buildPayload = (values: PrepFormValues, config: PrepFormConfig) => {
  const fields: HsField[] = [];
  pushIfPresent(fields, FIELD.firstName, values.firstName);
  pushIfPresent(fields, FIELD.lastName, values.lastName);
  pushIfPresent(fields, FIELD.email, values.email);
  pushIfPresent(fields, FIELD.mobile, values.mobile);
  pushIfPresent(fields, FIELD.school, values.school);
  pushIfPresent(fields, FIELD.serviceId, values.serviceId);
  pushIfPresent(fields, FIELD.brand, values.brand);
  pushIfPresent(fields, FIELD.zip, values.zip);
  pushIfPresent(fields, FIELD.firstYear, values.firstYear);
  UTM_FIELDS.forEach(utm => pushIfPresent(fields, utm, values.utms[utm]));

  const context: Record<string, string> = {};
  if (typeof window !== "undefined") {
    context.pageUri = window.location.href;
    context.pageName = typeof document !== "undefined" ? document.title : "";
  }
  if (!config.omitHutk) {
    const hutk = getHubspotUtk();
    if (hutk) context.hutk = hutk; // omit entirely if missing — never send an empty value
  }

  return {
    submittedAt: Date.now().toString(),
    fields,
    context,
    legalConsentOptions: {
      consent: {
        consentToProcess: true,
        text: config.consentToProcessText,
        communications: [
          {
            value: config.communicationsConsent,
            subscriptionTypeId: config.subscriptionTypeId,
            text: config.communicationsConsentText,
          },
        ],
      },
    },
  };
};

// Dry-runs (logs the payload, no POST) until portalId/formGuid are set.
export const submitPrepForm = async (
  values: PrepFormValues,
  config: PrepFormConfig,
): Promise<SubmitResult> => {
  const payload = buildPayload(values, config);

  if (!config.portalId || !config.formGuid) {
    // eslint-disable-next-line no-console
    console.log("[PrepEnquiryForm dry-run] would POST payload:", JSON.stringify(payload, null, 2));
    return { ok: true, dryRun: true };
  }

  try {
    const response = await fetch(SUBMIT_ENDPOINT(config.portalId, config.formGuid), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (response.ok) {
      return { ok: true, status: response.status };
    }

    let detail = "";
    try {
      const body = await response.json();
      detail = body?.errors?.[0]?.message || body?.message || "";
    } catch {
      /* non-JSON error body */
    }
    // eslint-disable-next-line no-console
    console.error("[PrepEnquiryForm] submit failed", response.status, detail);
    return { ok: false, status: response.status, error: detail };
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error("[PrepEnquiryForm] network error", err);
    return { ok: false, error: "network" };
  }
};
